import React, { useContext, useEffect, useState, useRef } from "react";
import TertiaryButton from "../buttons/TertiaryButton";
import Image from "../images/Image";
import { useNavigate } from "react-router-dom";
import { signOut } from "@aws-amplify/auth";
import GlobalContext from "../Context/GlobalContext";
import CustomModal from "../CustomModal";
import { showToast } from "../toastMessage/toastSlice";
import { useDispatch, useSelector } from "react-redux";
import LanguageChanger from "../LanguageChangerDropdown";
import { SideBarContent } from "./SideBarContent";
import useTranslation from "../CustomHooks/useTranslation";
import { fetchAuthSession } from 'aws-amplify/auth';
import NotificationDropdown from "../NotificationDropdown/NotificationDropdown";
import useApi from "../../services/useApi";
import endpoints from "../../services/endpoints";
import { RootState } from "../../store";

const Topbar: React.FC = () => {
  const buttons = ['Career', 'Blog', 'Job Application'];
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { setIsLoggedIn } = useContext(GlobalContext)
  const [isOpen, setIsOpen] = useState(false)
  const [isNotificationOpen, setIsNotificationOpen] = useState(false)
  const [hasUnreadNotifications, setHasUnreadNotifications] = useState(false)
  const notificationRef = useRef<HTMLDivElement>(null);
  const { translate } = useTranslation(SideBarContent);
  const [initials, setInitials] = useState('')
  const { getAPI } = useApi()
  const userInfo = useSelector((state: RootState) => state.userEmail.userInfo);

  const clickLogout = () => {
    setIsLoggedIn(false)
    signOut();
    localStorage.clear();
    dispatch(showToast({ type: "success", message: "Logout successful" }));
    navigate('/');
    setIsOpen(false)
  }

  const getUserName = async () => {
    // First, try to get initials from Redux store (after page loads)
    const firstName = typeof userInfo?.first_name === 'string' ? userInfo.first_name : '';
    const lastName = typeof userInfo?.last_name === 'string' ? userInfo.last_name : '';
    
    // If first_name exists, generate initials from first_name and last_name
    if (firstName) {
      const first = firstName.trim();
      const last = (lastName || '').trim();
      
      // If both first_name and last_name exist: first letter of first_name + first letter of last_name
      if (first && last) {
        setInitials((first[0] + last[0]).toUpperCase());
        return;
      } 
      // If only first_name exists: first letter of first_name
      else if (first) {
        setInitials(first[0].toUpperCase());
        return;
      }
    }

    // Fallback: try to get from auth session token (same way)
    const user = await fetchAuthSession({ forceRefresh: true });
    if (user) {
      const key = user?.tokens?.idToken?.payload?.name;
      if (!key || typeof key !== "string") return "";

      const words = key.trim().split(/\s+/); // Split by spaces

      // If multiple words: first letter of first word + first letter of second word
      if (words.length > 1) {
        setInitials((words[0][0] + words[1][0]).toUpperCase());
      } 
      // If single word: first letter only
      else if (words[0].length > 0) {
        setInitials(words[0][0].toUpperCase());
      }
    }
  }

  const checkUnreadNotifications = async () => {
    try {
      const response = await getAPI(`${endpoints.notifications}?unread_notification=true`);
      if (!response.error && response.data) {
        setHasUnreadNotifications(response.data.unread_notification || false);
      }
    } catch (error) {
      console.error("Error checking unread notifications:", error);
    }
  };

  useEffect(() => {
    getUserName();
    checkUnreadNotifications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInfo]);
  return (
    <div className="bg-white  flex justify-end items-center h-[100px]">
      {/* <h1 className="text-lg font-semibold">Admin Panel</h1> */}
      <div className=" w-fit flex gap-[50px] py-5 pr-10 items-center">
        {buttons.map((buttonItem) => (
          <TertiaryButton key={buttonItem} buttonName={translate(buttonItem)} id={buttonItem} />
        ))}
        <div className=" text-p_primary subTitle flex items-center">
          <LanguageChanger image="chevron-down-blue" />
        </div>
        <div data-testid="profile" className="h-[60px] w-[60px] bg-p_light rounded-full flex items-center justify-center cursor-pointer" onClick={() => navigate('/view-profile')}>
          <p className="w-full text-center">{initials}</p>
        </div>
        <div className="relative flex-shrink-0" ref={notificationRef}>
          <div className="relative">
            <Image 
              src="notifications" 
              className="h-6 w-6 cursor-pointer" 
              onClick={() => setIsNotificationOpen(!isNotificationOpen)} 
              testId={'notifications'} 
            />
            {hasUnreadNotifications && (
              <div className="absolute top-0 right-0 h-2 w-2 bg-red-500 rounded-full" />
            )}
          </div>
          <NotificationDropdown 
            isOpen={isNotificationOpen} 
            onClose={() => setIsNotificationOpen(false)}
            bellIconRef={notificationRef}
            onUnreadStatusChange={setHasUnreadNotifications}
          />
        </div>
        <Image src="log-out" className="h-6 w-6 cursor-pointer flex-shrink-0" onClick={() => setIsOpen(true)} testId={'logout'} />
        <CustomModal
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          title="Logout"
          subText="Are you sure you want to logout?"
          primaryButtonText="Logout"
          secondaryButtonText="Cancel"
          handelSubmit={clickLogout}
          buttonWidth="w-[85px]"
        />
      </div>
    </div>
  );
};

export default Topbar;